import clsx from "clsx";
import Image from "next/legacy/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";

import {
  Button,
  Card,
  FloatingActionBar,
  Hr,
  PriceTag,
  Receipt,
} from "@chef/components";
import { CurrencyWrapper as Currency } from "@chef/smart-components";
import { BRAND_PATHS, language } from "@chef/constants";
import {
  isGroceryBasketType,
  isMealboxBasketType,
  isMealboxProduct,
} from "@chef/helpers";
import { useIntercom } from "@chef/hooks";
import { Breakfast, ChevronUp, Plus } from "@chef/icons/small";
import {
  resetDeviations,
  selectAddonProducts,
  showNotification,
  useBillingQuery,
  useUpdateAgreementBasketProductsMutation,
} from "@chef/state-management";

import {
  getBasketFinances,
  getImageFromImages,
} from "@chef/state-management/helpers";

import { useAppDispatch, useTrack } from "@chef/state-management/hooks";

import { useAllVariations } from "../../../components/MySubscription/StandaloneBasketOverviewModal";

import { intl } from "./SubscriptionConfirmationPage.Intl";

export const SubscriptionConfirmationPage = () => {
  useIntercom(false);

  return (
    <>
      <h1 className="hidden lg:block col-span-full">{intl.CONFIRM_ORDER}</h1>

      <div className="col-span-full lg:col-span-3 lg:order-3">
        <ReceiptSection />
      </div>

      <div className="flex justify-between mt-4 lg:order-1 col-span-full lg:col-span-5 xl:col-span-9 lg:mt-10">
        <AddonHeader />
      </div>

      <div className="flex items-start col-span-full lg:col-span-5 xl:col-span-9 lg:order-2">
        <Addons />
      </div>
    </>
  );
};

const AddonHeader = () => {
  return (
    <>
      <h2 className="hidden text-lg lg:block">{intl.STANDALONE_PRODUCTS}</h2>
      <p className="lg:hidden">
        <strong>{intl.STANDALONE_PRODUCTS}</strong>
      </p>
      <Link
        href={BRAND_PATHS.ADDONS_SUBSCRIPTION_PATH}
        passHref
        className="text-sm underline text-primary"
      >
        {intl.CHANGE_STANDALONE_PRODUCTS_LINK_TEXT}
      </Link>
    </>
  );
};

const Addons = () => {
  const addonProducts = useSelector(selectAddonProducts);
  const { allVariations } = useAllVariations();

  if (addonProducts.length === 0) {
    return (
      <div>
        <p>{intl.YOU_HAVE_NOT_ADDED_ANY_ADDONS_TO_YOUR_SUBSCRIPTION}</p>
        <Link href={BRAND_PATHS.ADDONS_SUBSCRIPTION_PATH} legacyBehavior>
          <Button outlined IconLeft={Plus} className="mt-5">
            {intl.ADD_ADDONS_BUTTON_TEXT}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <ul className="grid gap-4 lg:gap-6 lg:grid-cols-2 xl:grid-cols-3 grow">
      {addonProducts.map(({ variationId, quantity, productId }) => {
        const variation = allVariations.find(
          (variation) => variation.variationId === variationId,
        );

        const imgSrc = getImageFromImages({
          images: variation?.images || [],
          size: "MEDIUM",
        });

        return (
          <Card
            key={variationId}
            id={productId + variationId}
            noPadding
            className="flex items-center"
          >
            <Image src={imgSrc} alt={variation?.name} width={80} height={99} />

            <p className="ml-2">
              {quantity} x {variation?.name}
            </p>
          </Card>
        );
      })}
    </ul>
  );
};

const ReceiptSection = () => {
  const { allVariations } = useAllVariations();

  const addonProducts = useSelector(selectAddonProducts);
  const { addonSum, addonCount } = addonProducts.reduce(
    ({ addonSum, addonCount }, product) => {
      return {
        addonSum: addonSum + product.price * product.quantity,
        addonCount: addonCount + product.quantity,
      };
    },
    { addonSum: 0, addonCount: 0 },
  );

  const [expanded, setExpanded] = useState(true);

  return (
    <div className="flex flex-col items-center" id="full-overview-card">
      <Receipt hideDetailsButton className="w-full">
        <Receipt.Header language={language}>
          <Receipt.Title language={language}>
            {intl.ENJOY_YOUR_MEAL}
          </Receipt.Title>
        </Receipt.Header>

        <div className="mt-4">
          <p>
            <strong>{intl.DETAILED_OVERVIEW}</strong>
          </p>

          <Hr className="my-3" />

          <Receipt.Line>
            <Receipt.LineIcon>
              <Breakfast className="w-5 h-5" />
            </Receipt.LineIcon>

            <Receipt.LineText>
              <div
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => setExpanded(!expanded)}
              >
                <p>{intl.FORMAT_ADDON_COUNT(addonCount)}</p>

                <ChevronUp
                  className={clsx("w-3 h-3", expanded && "rotate-180")}
                />
              </div>
            </Receipt.LineText>

            <PriceTag language={language}>{addonSum}</PriceTag>
          </Receipt.Line>

          {expanded && (
            <ul className="pl-10 mt-1.5">
              {addonProducts.map(({ price, variationId, quantity }) => {
                const variation = allVariations.find(
                  (variation) => variation.variationId === variationId,
                );

                return (
                  <Receipt.Line key={variationId}>
                    <p className="mr-4 truncate grow">
                      {quantity} x {variation?.name}
                    </p>

                    <Currency>{price * quantity}</Currency>
                  </Receipt.Line>
                );
              })}
            </ul>
          )}

          <Hr className="my-3" />

          <Receipt.Line>
            <Receipt.LineText>
              <strong>{intl.TOTAL}</strong>
            </Receipt.LineText>

            <strong>
              <Currency>{addonSum}</Currency>
            </strong>
          </Receipt.Line>
        </div>
      </Receipt>

      <div className="flex self-center gap-1 mt-4 text-xs">
        <p className="ml-2">{intl.DISCLAIMER}</p>
        <Link
          href={BRAND_PATHS.TOS_PATH}
          passHref
          className="underline text-primary"
        >
          {intl.TOS_LINK_TEXT}
        </Link>
      </div>
    </div>
  );
};

export const SubscriptionConfirmationActionBar = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const track = useTrack();
  useEffect(() => {
    track("updateSubscriptionStepViewed", {
      affiliation: "Frontend process",
      change_subscription_details: "Confirm addons",
      url: window.location.href,
    });
  }, [track]);

  const addonProducts = useSelector(selectAddonProducts);
  const price = getBasketFinances({
    basketProducts: addonProducts || [],
  }).addon;

  const { data: billingQuery } = useBillingQuery();

  const [updateAgreementBasketProducts, { isLoading }] =
    useUpdateAgreementBasketProductsMutation();

  const billing = billingQuery?.billing;

  const submit = async () => {
    if (!billing) {
      console.error("submit called before billing loaded");
      return;
    }

    try {
      const groceryBasket = billing.baskets?.find(isGroceryBasketType);

      const mealboxBasketProduct = billing.baskets
        ?.find(isMealboxBasketType)
        ?.basketProducts.find((bp) => isMealboxProduct(bp.variation.product));

      if (!mealboxBasketProduct) {
        console.error(
          "Attempted to update addons subscription without existing mealboxProduct in billing",
        );
        showNotification({
          type: "error",
          message: intl.GENERIC_ERROR_MESSAGE,
        });
        return;
      }

      await updateAgreementBasketProducts({
        basketId: groceryBasket?.basketId,
        products: addonProducts.map(({ variationId, quantity }) => ({
          variationId,
          quantity,
          deliveryWeekType: 10,
          isExtra: false,
        })),
      }).unwrap();

      dispatch(resetDeviations());

      track("updateSubscriptionStepCompleted", {
        affiliation: "Frontend process",
        changed_subscription_details: "Confirm addons",
        url: window.location.href,
      });

      router.push(BRAND_PATHS.MY_SUBSCRIPTION_LINK);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <FloatingActionBar>
      <div
        className="col-span-1 col-start-1 md:col-start-9 md:text-right"
        id="total-price"
      >
        <strong>
          <p className="text-sm underline">{intl.ACTION_BAR_TOTAL}</p>
          <div>
            <Currency>{price}</Currency>
          </div>
        </strong>
      </div>

      <Button
        onClick={submit}
        primary
        className="col-span-3 col-start-10 no-underline"
        id="floating-editor-bar-btn"
        loading={isLoading}
      >
        {intl.ACTION_BAR_CONFIRM_BUTTON}
      </Button>
    </FloatingActionBar>
  );
};
